import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { ERouterName } from '/@/types/index'
import CreatePlan from '/@/components/task/CreatePlan.vue'
import WaylinePanel from '/@/pages/page-web/projects/wayline.vue'
import DockPanel from '/@/pages/page-web/projects/dock.vue'
import LiveAgora from '/@/components/livestream-agora.vue'
import LiveOthers from '/@/components/livestream-others.vue'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    redirect: '/' + ERouterName.PROJECT
  },
  // 首页
  {
    path: '/' + ERouterName.PROJECT,
    name: ERouterName.PROJECT,
    component: () => import('/@/pages/page-web/index.vue')
  },
  // members, devices, workspaces
  {
    path: '/' + ERouterName.HOME,
    name: ERouterName.HOME,
    component: () => import('/@/pages/page-web/home.vue'),
    children: [
      {
        path: '/' + ERouterName.MEMBERS,
        name: ERouterName.MEMBERS,
        component: () => import('/@/pages/page-web/projects/members.vue')
      },
      {
        path: '/' + ERouterName.DEVICES,
        name: ERouterName.DEVICES,
        component: () => import('/@/pages/page-web/projects/devices.vue')
      },
      {
        path: '/' + ERouterName.FIRMWARES,
        name: ERouterName.FIRMWARES,
        component: () => import('../pages/page-web/projects/Firmwares.vue')
      },
      {
        path: '/' + ERouterName.WORKSPACES,
        name: ERouterName.WORKSPACES,
        component: () => import('/@/pages/page-web/workspaces/WorkspaceSelector.vue')
      }
    ]
  },
  // workspace
  {
    path: '/' + ERouterName.WORKSPACE,
    name: ERouterName.WORKSPACE,
    component: () => import('/@/pages/page-web/projects/workspace.vue'),
    redirect: '/' + ERouterName.TSA,
    children: [
      {
        path: '/' + ERouterName.TSA,
        component: () => import('/@/pages/page-web/projects/tsa.vue')
      },
      {
        path: '/' + ERouterName.LIVESTREAM,
        name: ERouterName.LIVESTREAM,
        component: () => import('/@/pages/page-web/projects/livestream.vue'),
        children: [
          {
            path: ERouterName.LIVING,
            name: ERouterName.LIVING,
            components: {
              LiveAgora,
              LiveOthers
            }
          }
        ]
      },
      {
        path: '/' + ERouterName.LAYER,
        name: ERouterName.LAYER,
        component: () => import('/@/pages/page-web/projects/layer.vue')
      },
      {
        path: '/' + ERouterName.MEDIA,
        name: ERouterName.MEDIA,
        component: () => import('/@/pages/page-web/projects/media.vue')
      },
      {
        path: '/' + ERouterName.WAYLINE,
        name: ERouterName.WAYLINE,
        component: () => import('/@/pages/page-web/projects/wayline.vue')
      },
      {
        path: '/' + ERouterName.TASK,
        name: ERouterName.TASK,
        component: () => import('/@/pages/page-web/projects/task.vue'),
        children: [
          {
            path: ERouterName.CREATE_PLAN,
            name: ERouterName.CREATE_PLAN,
            component: CreatePlan,
            children: [
              {
                path: ERouterName.SELECT_PLAN,
                name: ERouterName.SELECT_PLAN,
                components: {
                  WaylinePanel,
                  DockPanel
                }
              }
            ]
          }

        ]
      },
      {
        path: '/' + ERouterName.FLIGHT_AREA,
        name: ERouterName.FLIGHT_AREA,
        component: () => import('/@/pages/page-web/projects/flight-area.vue')
      },
      {
        path: '/' + ERouterName.WORKSPACE_MEMBERS,
        name: ERouterName.WORKSPACE_MEMBERS,
        component: () => import('/@/pages/page-web/projects/WorkspaceMembers.vue')
      },
      {
        path: '/' + ERouterName.WEATHER,
        name: ERouterName.WEATHER,
        component: () => import('/@/pages/page-web/projects/weather.vue')
      },
    ]
  },
  // pilot
  {
    path: '/' + ERouterName.PILOT,
    name: ERouterName.PILOT,
    component: () => import('/@/pages/page-pilot/pilot-index.vue'),
  },
  {
    path: '/' + ERouterName.PILOT_HOME,
    component: () => import('/@/pages/page-pilot/pilot-home.vue')
  },
  {
    path: '/' + ERouterName.PILOT_WORKSPACE_SELECT,
    component: () => import('/@/pages/page-pilot/pilot-workspace-select.vue')
  },
  {
    path: '/' + ERouterName.PILOT_MEDIA,
    component: () => import('/@/pages/page-pilot/pilot-media.vue')
  },
  {
    path: '/' + ERouterName.PILOT_LIVESHARE,
    component: () => import('/@/pages/page-pilot/pilot-liveshare.vue')
  },
  {
    path: '/' + ERouterName.PILOT_BIND,
    component: () => import('/@/pages/page-pilot/pilot-bind.vue')
  },
  // Lien de partage public (sans authentification) — visualisation d'un live via UUID
  {
    path: '/share/:uuid',
    name: 'share',
    component: () => import('/@/pages/page-web/share.vue')
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

// Route guards
router.beforeEach((to, _from, next) => {
  const userType = parseInt(localStorage.getItem('user_type') || '1')
  const token = localStorage.getItem('x-auth-token')

  // Redirect Observers away from members page (back to workspace selector)
  if (to.path.includes('/members') && userType === 5) {
    next('/' + ERouterName.WORKSPACES)
    return
  }

  // Prevent restricted users (Unassigned=0, CTA-CODIS=3, Observer=5) from accessing
  // workspace routes if they haven't been approved (no workspaceId in localStorage)
  const restrictedUser = userType === 0 || userType === 3 || userType === 5 || userType === 6
  const workspaceRoutes = [
    ERouterName.TSA, ERouterName.LIVESTREAM, ERouterName.LAYER,
    ERouterName.MEDIA, ERouterName.WAYLINE, ERouterName.TASK, ERouterName.FLIGHT_AREA
  ]
  const isWorkspaceRoute = workspaceRoutes.some(r => to.path.includes('/' + r))
  const workspaceId = localStorage.getItem('workspace_id')
  if (restrictedUser && isWorkspaceRoute && !workspaceId) {
    next('/' + ERouterName.WORKSPACES)
    return
  }

  next()
})

export default router
